import * as THREE from 'three';
import type { HumanFace, HumanProportions } from '../../../content/characters/rigs/human.js';
import { headSurfaceKey, headwearSurface } from './human-head.js';

/** The authored headwear cage, in sculpt coordinates. Geometry and hair room use the same reversible fitting. */
export const HEADWEAR_HEAD = { cy: 1.889, cz: -.012, rx: .088, ry: .1, rz: .11, brow: 1.88, eyes: 1.858, ears: 1.843 };
export interface HeadwearShape {
  key: string;
  to(point: THREE.Vector3): THREE.Vector3;
  from(point: THREE.Vector3): THREE.Vector3;
}
const shapes = new Map<string, HeadwearShape>();
const fade = (y: number) => { const t = THREE.MathUtils.clamp((y - 1.74) / .1, 0, 1); return t * t * (3 - 2 * t); };

/** Sample the actual sculpt along rays once, preserving each item's clearance and decorative lengths outside the head. */
export function createHeadwearShape(p: HumanProportions, face: HumanFace = p.face): HeadwearShape {
  const key = `${headSurfaceKey(p)}:headwear:${JSON.stringify(face)}`, known = shapes.get(key);
  if (known) return known;
  const field = headwearSurface(p, face), polar = 24, around = 48, radii = new Float64Array((polar + 1) * (around + 1));
  const { cy, cz, rx, ry, rz } = HEADWEAR_HEAD;
  for (let row = 0; row <= polar; row++) for (let col = 0; col <= around; col++) {
    const phi = row / polar * Math.PI, theta = col / around * Math.PI * 2;
    const x = Math.sin(phi) * Math.sin(theta), y = Math.cos(phi), z = Math.sin(phi) * Math.cos(theta);
    let low = 0, high = .35;
    for (let step = 0; step < 22; step++) {
      const r = (low + high) / 2;
      if (field(x * r, cy + y * r, cz + z * r) < 0) low = r; else high = r;
    }
    // Small angular variations remain cloth-scale curves instead of embossing eyelids or nostrils into the piece.
    radii[row * (around + 1) + col] = (low + high) / 2;
  }
  const change = (x: number, y: number, z: number) => {
    const row = Math.acos(THREE.MathUtils.clamp(y, -1, 1)) / Math.PI * polar;
    const col = ((Math.atan2(x, z) / (2 * Math.PI) + 1) % 1) * around;
    const r = Math.min(polar - 1, Math.floor(row)), c = Math.min(around - 1, Math.floor(col)), v = row - r, u = col - c;
    const a = radii[r * (around + 1) + c] * (1 - u) + radii[r * (around + 1) + c + 1] * u;
    const b = radii[(r + 1) * (around + 1) + c] * (1 - u) + radii[(r + 1) * (around + 1) + c + 1] * u;
    const source = 1 / Math.sqrt((x / rx) ** 2 + (y / ry) ** 2 + (z / rz) ** 2);
    const delta = a * (1 - v) + b * v - source;
    // Only the lower, loose part is limited, keeping its shoulder fade monotone and reversible.
    return y < 0 ? Math.min(delta, .06) : delta;
  };
  const map = (point: THREE.Vector3, inverse: boolean) => {
    const dx = point.x, dy = point.y - cy, dz = point.z - cz, length = Math.hypot(dx, dy, dz);
    if (length < 1e-8 || point.y <= 1.74) return point;
    const x = dx / length, y = dy / length, z = dz / length, delta = change(x, y, z);
    let radius = length;
    if (inverse) {
      radius = length - delta;
      if (cy + y * radius < 1.84) {
        let low = Math.max(0, length - Math.abs(delta) - .001), high = length + Math.abs(delta) + .001;
        for (let i = 0; i < 20; i++) {
          radius = (low + high) / 2;
          if (radius + delta * fade(cy + y * radius) < length) low = radius; else high = radius;
        }
      }
    } else radius += delta * fade(point.y);
    return point.set(x * radius, cy + y * radius, cz + z * radius);
  };
  const shape: HeadwearShape = { key, to: point => map(point, false), from: point => map(point, true) };
  if (shapes.size >= 32) shapes.delete(shapes.keys().next().value!);
  shapes.set(key, shape); return shape;
}
